import React, { useState, useMemo, useRef, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { usePopup } from '@/contexts/PopupContext'

interface CustomerFormPopupProps {
  isOpen: boolean
  onClose: () => void
}

// Các tùy chọn email phổ biến
const EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com'
]

// Các tùy chọn độ tuổi
const AGE_RANGES = [
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55+'
]

type Gender = 'male' | 'female' | 'other'

interface CustomerFormData {
  fullName: string
  phone: string
  email: string
  gender: Gender | null
  ageRange: string
  note: string
}

interface ValidationErrors {
  fullName?: string
  phone?: string
  email?: string
}

export const CustomerFormPopup: React.FC<CustomerFormPopupProps> = ({ isOpen, onClose }) => {
  const { openPopup, closePopup } = usePopup()
  const prevIsOpen = React.useRef(isOpen)
  React.useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      openPopup()
    }
    if (prevIsOpen.current && !isOpen) {
      closePopup()
    }
    prevIsOpen.current = isOpen
  }, [isOpen])

  const [formData, setFormData] = useState<CustomerFormData>({
    fullName: '',
    phone: '',
    email: '',
    gender: null,
    ageRange: '',
    note: ''
  })
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [hasMoreAgeRanges, setHasMoreAgeRanges] = useState(false)
  const ageRangesRef = useRef<HTMLDivElement>(null)

  const [emailPrefix, setEmailPrefix] = useState('')
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false)

  // Kiểm tra form hợp lệ
  const isFormValid = useMemo(() => {
    return (
      formData.fullName.trim() !== '' &&
      formData.phone.trim() !== '' &&
      formData.gender !== null &&
      formData.ageRange !== '' &&
      (!formData.email || formData.email.includes('@')) // Email không bắt buộc nhưng nếu có phải hợp lệ
    )
  }, [formData])

  // Kiểm tra scroll độ tuổi
  useEffect(() => {
    const container = ageRangesRef.current
    if (container) {
      const hasMore = container.scrollWidth > container.clientWidth
      setHasMoreAgeRanges(hasMore)
    }
  }, [])

  // Validate form
  const validateForm = () => {
    const newErrors: ValidationErrors = {}

    // Validate họ tên
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Vui lòng nhập họ tên'
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Họ tên phải có ít nhất 2 ký tự'
    }

    // Validate số điện thoại
    const phoneRegex = /(84|0[3|5|7|8|9])+([0-9]{8})\b/
    if (!formData.phone.trim()) {
      newErrors.phone = 'Vui lòng nhập số điện thoại'
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Số điện thoại không hợp lệ'
    }

    // Validate email nếu có
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Email không hợp lệ'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Xử lý khi thay đổi email
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormData(prev => ({ ...prev, email: value }))

    // Kiểm tra xem có @ không để hiển thị gợi ý
    const atIndex = value.indexOf('@')
    if (atIndex !== -1) {
      setEmailPrefix(value.slice(0, atIndex))
      setShowEmailSuggestions(true)
    } else {
      setEmailPrefix(value)
      setShowEmailSuggestions(false)
    }
  }

  // Xử lý khi chọn domain email
  const handleSelectDomain = (domain: string) => {
    setFormData(prev => ({ ...prev, email: `${emailPrefix}@${domain}` }))
    setShowEmailSuggestions(false)
  }

  // Xử lý khi submit form
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (!isFormValid) {
      toast.error('Vui lòng điền đầy đủ thông tin bắt buộc')
      return
    }

    // TODO: Xử lý lưu thông tin khách hàng
    console.log('Form data:', formData)
    toast.success('Đã thêm khách hàng thành công!')
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Popup */}
      <div className="fixed p-2 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:max-w-[420px] max-w-[90%] max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Thêm khách hàng mới
            </h2>
            <p className="text-sm text-gray-500 mt-1 line-clamp-1">
              Khách sẽ được thêm vào danh sách của bạn
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="overflow-y-auto">
            {/* Họ tên */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Họ tên <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={e => {
                  setFormData(prev => ({ ...prev, fullName: e.target.value }))
                  setErrors(prev => ({ ...prev, fullName: undefined }))
                }}
                className={`w-full px-3 py-2 border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Nhập họ tên khách hàng"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>
              )}
            </div>

            {/* Số điện thoại */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Số điện thoại <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => {
                  setFormData(prev => ({ ...prev, phone: e.target.value }))
                  setErrors(prev => ({ ...prev, phone: undefined }))
                }}
                className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Nhập số điện thoại"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>

            {/* Email */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => {
                  handleEmailChange(e)
                  setErrors(prev => ({ ...prev, email: undefined }))
                }}
                className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Nhập email (không bắt buộc)"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
              {/* Email suggestions */}
              {showEmailSuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {EMAIL_DOMAINS.map(domain => (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => handleSelectDomain(domain)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                    >
                      {`${emailPrefix}@${domain}`}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Giới tính <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex overflow-x-auto no-scrollbar bg-gray-50 rounded-lg">
                  <div className="flex p-1 gap-0.5 min-w-full">
                    {[
                      { value: 'male' as Gender, label: 'Nam' },
                      { value: 'female' as Gender, label: 'Nữ' },
                      { value: 'other' as Gender, label: 'Khác' }
                    ].map((option, index) => (
                      <React.Fragment key={option.value}>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, gender: option.value }))}
                          className={`
                          shrink-0 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200
                          ${formData.gender === option.value
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                            }
                        `}
                        >
                          {option.label}
                        </button>
                        {index < 2 && (
                          <div className="self-center text-gray-300 px-1">|</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Độ tuổi <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div
                  ref={ageRangesRef}
                  className="flex overflow-x-auto no-scrollbar bg-gray-50 rounded-lg"
                >
                  <div className="flex p-1 gap-0.5 min-w-full">
                    {AGE_RANGES.map((range, index) => (
                      <React.Fragment key={range}>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, ageRange: range }))}
                          className={`
                          shrink-0 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200
                          ${formData.ageRange === range
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                            }
                        `}
                        >
                          {range.replace('-', '–')} {/* Sử dụng dấu gạch ngang en dash */}
                        </button>
                        {index < AGE_RANGES.length - 1 && (
                          <div className="self-center text-gray-300 px-1">|</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                {hasMoreAgeRanges && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gradient-to-l from-gray-50 to-transparent pointer-events-none">
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Thêm CSS cho việc ẩn scrollbar nhưng vẫn scroll được */}
            <style>
              {`
              .no-scrollbar {
                -ms-overflow-style: none;  /* IE and Edge */
                scrollbar-width: none;  /* Firefox */
              }
              .no-scrollbar::-webkit-scrollbar {
                display: none;  /* Chrome, Safari and Opera */
              }
            `}
            </style>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ghi chú
              </label>
              <textarea
                value={formData.note}
                onChange={e => setFormData(prev => ({ ...prev, note: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập ghi chú (không bắt buộc)"
                rows={2}
              />
            </div>
          </div>
          <div>
            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid}
              className={`
              w-full py-2.5 px-4 font-medium rounded-lg transition-colors duration-200
              ${isFormValid
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
            `}
            >
              Thêm khách
            </button>
          </div>


        </form>
      </div>
    </>
  )
} 